import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface SendBackReviewPayload {
	comment: string;
	attachments: File[];
}

@Injectable({
	providedIn: 'root',
})
export class SendBackReviewPayloadService {
	private readonly payloadByFormId = new Map<string, { payload: SendBackReviewPayload; createdAt: number }>();
	private static readonly PAYLOAD_TTL_MS = 5 * 60 * 1000;

	constructor(router: Router) {
		router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
			this.payloadByFormId.clear();
		});
	}

	set(formId: string, payload: SendBackReviewPayload): void {
		this.clearExpired();
		this.payloadByFormId.set(formId, { payload, createdAt: Date.now() });
	}

	/**
	 * Returns the stashed payload WITHOUT discarding it.
	 *
	 * Reading and discarding in one step loses the comment and attachments whenever the request
	 * is retried: the interceptor chain re-runs on a replay, and the second pass would find
	 * nothing and send an empty body, which the server accepts as a send-back with no comment.
	 * The caller discards via {@link clear} once the submission has actually resolved.
	 */
	peek(formId: string): SendBackReviewPayload | undefined {
		this.clearExpired();
		return this.payloadByFormId.get(formId)?.payload;
	}

	clear(formId: string): void {
		this.payloadByFormId.delete(formId);
	}

	private clearExpired(): void {
		const now = Date.now();
		for (const [formId, entry] of this.payloadByFormId.entries()) {
			if (now - entry.createdAt > SendBackReviewPayloadService.PAYLOAD_TTL_MS) {
				this.payloadByFormId.delete(formId);
			}
		}
	}
}
